/* Build-in hooks */
import { useEffect, useState, useRef } from "react";

/* Custom hooks */
import useOutsideClick from "../../hooks/useOutsideClick";

/* Assets */
import ArrowDownIcon from "../../assets/icons/arrow-down.png";

import { twMerge } from "tailwind-merge";

export const DropdownItem = ({
  label,
  selected,
  onClick,
  showSelectMark = true,
  children,
  textClassName = "",
}) => {
  return (
    <div
      onClick={onClick}
      className={twMerge(
        "group/dropdownitem flex flex-row flex-nowrap items-center w-full min-h-[30px] sm:min-h-[40px] px-2 py-1 sm:px-3 sm:py-2 cursor-pointer whitespace-nowrap text-white text-[12px] sm:text-sm",
        selected ? "bg-[#2c2c2c] font-bold" : "hover:bg-[#333]",
        textClassName,
      )}
    >
      {children ? <>{children}</> : <>{label}</>}
      {selected && showSelectMark && (
        <div className="ml-auto transition-all animate-opacity-scale">
          <svg
            width="10"
            height="7"
            className="sm:w-[13px] sm:h-[9px]"
            viewBox="0 0 13 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.55975 6.99375L11.2691 0.284375C11.4275 0.126042 11.6122 0.046875 11.8233 0.046875C12.0344 0.046875 12.2191 0.126042 12.3775 0.284375C12.5358 0.442708 12.615 0.630861 12.615 0.848833C12.615 1.06681 12.5358 1.25469 12.3775 1.4125L5.11391 8.69583C4.95558 8.85417 4.77086 8.93333 4.55975 8.93333C4.34863 8.93333 4.16391 8.85417 4.00558 8.69583L0.601412 5.29167C0.443079 5.13333 0.367079 4.94544 0.373412 4.728C0.379746 4.51056 0.462343 4.3224 0.621204 4.16354C0.780065 4.00468 0.968218 3.92551 1.18566 3.92604C1.40311 3.92657 1.591 4.00574 1.74933 4.16354L4.55975 6.99375Z"
              fill="#52FAF0"
              fillOpacity="0.54"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

const DropDown = ({
  children,
  showItem,
  selected,
  limit = 5,
  containerClassName = "",
  buttonClassName = "",
  textClassName = "",
  dropdownClassName = "",
}) => {
  /* Local states */
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  /* Reference to the dropdown container for detecting outside clicks */
  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    if (isOpen) {
      setShowModal(true);
    } else {
      const timer = setTimeout(() => {
        setShowModal(false);
      }, 800); // Delay to allow for animation to finish

      // Clear the timeout if the component unmounts or isOpen changes
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <div className={twMerge("relative w-full", containerClassName)}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={twMerge(
          "bg-[#1e1e1e] px-3 py-1 sm:py-2 rounded-lg text-white cursor-pointer flex flex-row items-center justify-between flex-nowrap whitespace-nowrap gap-x-2 sm:gap-x-3",
          buttonClassName,
        )}
      >
        {/* Dropdown active label */}
        {showItem ? (
          showItem
        ) : (
          <span
            className={twMerge(
              "text-tertiary first-letter:uppercase lowercase font-medium text-[12px] md:text-base truncate",
              textClassName,
            )}
          >
            {selected}
          </span>
        )}

        <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0">
          <img
            src={ArrowDownIcon}
            alt="arrow"
            className={`w-auto h-full object-cover transition-transform duration-300 ${
              isOpen ? "rotate-180" : "rotate-0"
            } `}
          />
        </span>
      </div>

      {/* Dropdown items */}
      {showModal && (
        <div
          ref={dropdownRef}
          onClick={() => setIsOpen(false)}
          className={twMerge(
            "transition-opacity duration-300 absolute flex flex-col top-full mt-1 bg-[#1e1e1e] rounded-lg overflow-y-auto w-full z-50",
            isOpen ? "animate-opacity-scale" : "opacity-0",
            dropdownClassName,
          )}
          style={{
            overflowY: "auto",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default DropDown;
